import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useGetBannedKeywordsQuery,
  useAddBannedKeywordMutation,
  useBulkAddBannedKeywordsMutation,
  useDeleteBannedKeywordMutation,
  useCheckBannedWordsMutation,
} from '../../../store/api/adminApi';
import { showSuccess, showError } from '../../../utils/toast';
import PageLoader from '../../../components/PageLoader';
import Pagination from '../../../components/Pagination';
import { useLanguage } from '../../../context/LanguageContext';

const BannedKeywords: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(1);
  const [keyword, setKeyword]       = useState('');
  const [bulkText, setBulkText]     = useState('');
  const [showBulk, setShowBulk]     = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Test panel state
  const [testText, setTestText]           = useState('');
  const [testResult, setTestResult]       = useState<{ clean: boolean; matched: string[]; message: string } | null>(null);

  const { data, isLoading, error } = useGetBannedKeywordsQuery({ search, page, per_page: 20 });
  const [addKeyword,  { isLoading: isAdding }]  = useAddBannedKeywordMutation();
  const [bulkAdd,     { isLoading: isBulking }] = useBulkAddBannedKeywordsMutation();
  const [deleteKeyword]                         = useDeleteBannedKeywordMutation();
  const [checkBannedWords, { isLoading: isChecking }] = useCheckBannedWordsMutation();

  const keywords = data?.data?.data || [];
  const meta     = data?.data;

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!keyword.trim()) return;
    try {
      await addKeyword({ keyword: keyword.trim() }).unwrap();
      showSuccess(t('Keyword banned successfully.'));
      setKeyword('');
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to add keyword.'));
    }
  };

  const handleBulkAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bulkText.trim()) return;
    try {
      const res = await bulkAdd({ keywords: bulkText }).unwrap();
      showSuccess(res.message || t('Keywords added.'));
      setBulkText('');
      setShowBulk(false);
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to add keywords.'));
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await deleteKeyword(id).unwrap();
      showSuccess(t('Keyword removed.'));
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to remove keyword.'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleTest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testText.trim()) return;
    try {
      const res = await checkBannedWords(testText).unwrap();
      setTestResult({ clean: res.clean, matched: res.matched, message: res.message });
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to check text.'));
    }
  };

  if (isLoading) return <PageLoader />;
  if (error) return <div className="alert alert-danger m-5">{t('Error loading banned keywords.')}</div>;

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">

        {/* Toolbar */}
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {t('Banned Keywords')}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <span className="text-muted text-hover-primary cursor-pointer" onClick={() => navigate('/admin/dashboard')}>{t('Home')}</span>
                </li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">{t('Platform Management')}</li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">{t('Banned Keywords')}</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="row g-5">

              {/* Left column: Add + Test */}
              <div className="col-lg-4 d-flex flex-column gap-5">

                {/* Add Keyword Panel */}
                <div className="card">
                  <div className="card-header border-0 pt-6">
                    <h3 className="card-title fw-bold fs-4">{t('Add Banned Keyword')}</h3>
                    <div className="card-toolbar">
                      <button
                        className="btn btn-sm btn-light-primary"
                        onClick={() => setShowBulk(v => !v)}
                      >
                        {showBulk ? t('Single Add') : t('Bulk Add')}
                      </button>
                    </div>
                  </div>
                  <div className="card-body pt-4">
                    {!showBulk ? (
                      <form onSubmit={handleAdd}>
                        <div className="mb-4">
                          <label className="form-label fw-semibold">{t('Keyword')}</label>
                          <input
                            type="text"
                            className="form-control form-control-solid"
                            placeholder={t('e.g. spam, fake, scam')}
                            value={keyword}
                            onChange={e => setKeyword(e.target.value)}
                          />
                          <div className="text-muted fs-7 mt-2">{t('Stored lowercase. Checked in ad title & description.')}</div>
                        </div>
                        <button type="submit" className="btn btn-base w-100" disabled={isAdding || !keyword.trim()}>
                          {isAdding && <span className="spinner-border spinner-border-sm me-2"></span>}
                          {t('Add Keyword')}
                        </button>
                      </form>
                    ) : (
                      <form onSubmit={handleBulkAdd}>
                        <div className="mb-4">
                          <label className="form-label fw-semibold">{t('Keywords')}</label>
                          <textarea
                            className="form-control form-control-solid"
                            rows={6}
                            placeholder={'spam, fake\nscam\nillegal'}
                            value={bulkText}
                            onChange={e => setBulkText(e.target.value)}
                          />
                          <div className="text-muted fs-7 mt-2">{t('Comma or newline-separated. Duplicates skipped.')}</div>
                        </div>
                        <button type="submit" className="btn btn-base w-100" disabled={isBulking || !bulkText.trim()}>
                          {isBulking && <span className="spinner-border spinner-border-sm me-2"></span>}
                          {t('Add All Keywords')}
                        </button>
                      </form>
                    )}
                  </div>
                </div>

                {/* Test Text Panel */}
                <div className="card">
                  <div className="card-header border-0 pt-6">
                    <h3 className="card-title fw-bold fs-4">{t('Test Text')}</h3>
                  </div>
                  <div className="card-body pt-4">
                    <form onSubmit={handleTest}>
                      <div className="mb-4">
                        <label className="form-label fw-semibold">{t('Enter text to check')}</label>
                        <textarea
                          className="form-control form-control-solid"
                          rows={5}
                          placeholder={t('Paste an ad title or description here to check for banned words...')}
                          value={testText}
                          onChange={e => { setTestText(e.target.value); setTestResult(null); }}
                        />
                      </div>
                      <button
                        type="submit"
                        className="btn btn-light-primary w-100"
                        disabled={isChecking || !testText.trim()}
                      >
                        {isChecking
                          ? <><span className="spinner-border spinner-border-sm me-2"></span>{t('Checking...')}</>
                          : <><i className="fas fa-search me-2"></i>{t('Check Text')}</>
                        }
                      </button>
                    </form>

                    {testResult && (
                      <div className={`mt-4 alert ${testResult.clean ? 'alert-success' : 'alert-danger'} d-flex align-items-start gap-3`}>
                        <i className={`fas ${testResult.clean ? 'fa-check-circle' : 'fa-exclamation-triangle'} fs-4 mt-1`}></i>
                        <div>
                          <div className="fw-bold mb-1">{testResult.clean ? t('Text is clean') : t('Banned keywords found')}</div>
                          {!testResult.clean && (
                            <div className="d-flex flex-wrap gap-2 mt-2">
                              {testResult.matched.map(w => (
                                <span key={w} className="badge badge-danger px-3 py-2">{w}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              </div>

              {/* Keywords List */}
              <div className="col-lg-8">
                <div className="card">
                  <div className="card-header border-0 pt-6">
                    <div className="card-title">
                      <div className="d-flex align-items-center position-relative my-1">
                        <i className="fas fa-search position-absolute ms-4 text-muted"></i>
                        <input
                          type="text"
                          className="form-control form-control-solid w-250px ps-12"
                          placeholder={t('Search keywords...')}
                          value={search}
                          onChange={e => { setSearch(e.target.value); setPage(1); }}
                        />
                      </div>
                    </div>
                    <div className="card-toolbar">
                      <span className="badge badge-light-danger fs-6 fw-bold">
                        {meta?.total ?? 0} {meta?.total !== 1 ? t('keywords banned') : t('keyword banned')}
                      </span>
                    </div>
                  </div>

                  <div className="card-body pt-0">
                    {keywords.length === 0 ? (
                      <div className="text-center py-10 text-muted">
                        <i className="fas fa-ban fs-2x mb-3 d-block"></i>
                        {t('No banned keywords yet.')}
                      </div>
                    ) : (
                      <div className="d-flex flex-wrap gap-3 py-4">
                        {keywords.map((kw: any) => (
                          <div
                            key={kw.id}
                            className="d-flex align-items-center gap-2 badge badge-light-danger px-3 py-2 fw-semibold"
                            style={{ fontSize: '13px' }}
                          >
                            <i className="fas fa-ban text-danger me-1 fs-8"></i>
                            <span>{kw.keyword}</span>
                            <button
                              type="button"
                              className="btn btn-icon btn-sm btn-flush ms-1"
                              style={{ width: 18, height: 18 }}
                              onClick={() => handleDelete(kw.id)}
                              disabled={deletingId === kw.id}
                              title={t('Remove')}
                            >
                              {deletingId === kw.id
                                ? <span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10 }}></span>
                                : <i className="fas fa-times text-danger" style={{ fontSize: 11 }}></i>
                              }
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {meta && meta.last_page > 1 && (
                      <Pagination
                        currentPage={page}
                        lastPage={meta.last_page}
                        total={meta.total}
                        perPage={meta.per_page}
                        onPageChange={setPage}
                      />
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannedKeywords;
